import {
  ClipboardEvent,
  DragEvent,
  SubmitEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaTelegramPlane } from "react-icons/fa";
import {
  LuCheck,
  LuCheckCheck,
  LuFileText,
  LuImagePlus,
  LuPaperclip,
  LuSend,
  LuX,
} from "react-icons/lu";
import { Loader, Spinner } from "../../components/ui/Loader";
import {
  MAX_ATTACHMENT_BYTES,
  MessageBatch,
  MessagePartner,
  fetchMessageHistory,
  fetchMessagePartners,
  formatFileSize,
  sendPartnerMessage,
} from "../../lib/messages";
import { MdOutlineChat } from "react-icons/md";

const initialsOf = (name: string | null) =>
  (name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const Avatar = ({
  partner,
  className = "size-10",
}: {
  partner: MessagePartner;
  className?: string;
}) =>
  partner.avatar_url ? (
    <img
      src={partner.avatar_url}
      alt=""
      className={`shrink-0 rounded-full object-cover ${className}`}
    />
  ) : (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full bg-brand/20 font-semibold text-brand ${className}`}
      aria-hidden
    >
      {initialsOf(partner.name)}
    </span>
  );

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString("uk-UA", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

type Pending = { file: File; preview: string | null };

export const AdminMessages = () => {
  const [partners, setPartners] = useState<MessagePartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [history, setHistory] = useState<MessageBatch[]>([]);

  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessagePartners()
      .then(setPartners)
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, []);

  const partnerById = useMemo(
    () => new Map(partners.map((p) => [p.id, p])),
    [partners]
  );
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const selectedKey = selectedIds.slice().sort().join(",");
  const allSelected = partners.length > 0 && selected.size === partners.length;

  useEffect(() => {
    let cancelled = false;
    // History is shown for a single partner only.
    const ids = selectedKey && !selectedKey.includes(",") ? [selectedKey] : [];
    setHistory([]);
    fetchMessageHistory(ids)
      .then((h) => !cancelled && setHistory(h))
      .catch(() => !cancelled && setHistory([]));
    return () => {
      cancelled = true;
    };
  }, [selectedKey]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [history]);

  // Revoke object URLs of previews on unmount.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  useEffect(
    () => () => pendingRef.current.forEach((p) => p.preview && URL.revokeObjectURL(p.preview)),
    []
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(partners.map((p) => p.id)));

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    const files = Array.from(list);
    const tooBig = files.filter((f) => f.size > MAX_ATTACHMENT_BYTES);
    setSendError(
      tooBig.length
        ? `Файл завеликий (макс. ${formatFileSize(MAX_ATTACHMENT_BYTES)}): ${tooBig
            .map((f) => f.name)
            .join(", ")}`
        : null
    );
    setPending((prev) => [
      ...prev,
      ...files
        .filter((f) => f.size <= MAX_ATTACHMENT_BYTES)
        .map((file) => ({
          file,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        })),
    ]);
  };

  const removeFile = (idx: number) =>
    setPending((prev) => {
      const p = prev[idx];
      if (p?.preview) URL.revokeObjectURL(p.preview);
      return prev.filter((_, i) => i !== idx);
    });

  const canSend =
    !sending && selected.size > 0 && (text.trim().length > 0 || pending.length > 0);

  const handleSend = async (e?: SubmitEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!canSend) return;
    setSending(true);
    setSendError(null);
    setNotice(null);
    try {
      const result = await sendPartnerMessage({
        userIds: selectedIds,
        text,
        files: pending.map((p) => p.file),
      });
      pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPending([]);
      setText("");
      setHistory((h) => [...h, result.batch]);
      if (result.failed.length > 0) {
        const names = result.failed
          .map((f) => partnerById.get(f.user_id)?.name ?? "—")
          .join(", ");
        setSendError(`Не доставлено в Telegram: ${names}.`);
      } else {
        setNotice(
          `Надіслано ${result.delivered.length} ${
            result.delivered.length === 1 ? "партнеру" : "партнерам"
          }`
        );
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Не вдалося надіслати");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length > 0) {
      e.preventDefault();
      addFiles(e.clipboardData.files);
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const selectedPartners = selectedIds
    .map((id) => partnerById.get(id))
    .filter((p): p is MessagePartner => !!p);

  const recipientsLabel = (ids: string[]) => {
    const names = ids.map((id) => partnerById.get(id)?.name ?? "—");
    return names.length <= 3
      ? names.join(", ")
      : `${names.slice(0, 3).join(", ")} +${names.length - 3}`;
  };

  if (loading) return <Loader />;

  return (
    <main className="mx-auto flex w-full max-w-cabinet flex-1 flex-col px-5 pt-8 md:px-8">
      <h1 className="text-2xl font-bold text-white">Повідомлення</h1>

      {error ? (
        <p className="mt-7 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {error}
        </p>
      ) : (
        <section className="card mt-5 grid overflow-hidden sm:mt-7 md:h-[calc(100vh-158px)] md:min-h-[550px] md:grid-cols-[320px_1fr]">
          {/* Partners */}
          <aside className="flex min-h-0 flex-col border-b border-line md:border-b-0 md:border-r">
            <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-white">Партнери</h2>
              </div>
              <button
                type="button"
                onClick={toggleAll}
                disabled={partners.length === 0}
                className="link-brand text-xs disabled:opacity-40"
              >
                {allSelected ? "Зняти всі" : "Вибрати всі"}
              </button>
            </div>

            {partners.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-neutral-500">
                Немає партнерів з підключеним Telegram
              </p>
            ) : (
              <ul className="max-h-44 flex-1 scrollbar-dark space-y-0.5 overflow-y-auto p-2 md:max-h-none">
                {partners.map((p) => {
                  const isSelected = selected.has(p.id);
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => toggle(p.id)}
                        aria-pressed={isSelected}
                        className={`group flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition ${
                          isSelected ? "bg-brand/10" : "hover:bg-white/5"
                        }`}
                      >
                        <span className="relative">
                          <Avatar partner={p} />
                          {isSelected && (
                            <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-brand text-black ring-2 ring-panel">
                              <LuCheck className="text-[10px]" aria-hidden />
                            </span>
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block truncate text-sm font-semibold ${
                              isSelected ? "text-brand" : "text-white"
                            }`}
                          >
                            {p.name ?? "—"}
                          </span>
                          <span className="block truncate text-xs text-neutral-500">
                            {p.telegram_username ? `@${p.telegram_username}` : "—"}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Chat */}
          <div
            className="relative flex min-h-[560px] min-w-0 flex-col bg-[radial-gradient(ellipse_at_top_right,rgba(245,196,0,0.05),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(56,189,248,0.04),transparent_50%)] md:min-h-0"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
            }}
            onDrop={onDrop}
          >
            <header className="flex min-h-[57px] items-center gap-3 border-b border-line px-5 py-3">
              {selectedPartners.length === 0 ? (
                <p className="text-sm text-neutral-500">Оберіть отримувачів</p>
              ) : (
                <>
                  <div className="flex -space-x-2.5">
                    {selectedPartners.slice(0, 4).map((p) => (
                      <span key={p.id} className="rounded-full">
                        <Avatar partner={p} className="size-8 text-xs" />
                      </span>
                    ))}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-white">
                      {selectedPartners.length === 1
                        ? selectedPartners[0].name
                        : selectedPartners.map((p) => p.name?.split(/\s+/)[0] ?? "—").join(", ")}
                    </p>
                    <p className="flex items-center gap-1.5 truncate text-xs text-neutral-500">
                      <FaTelegramPlane className="text-sky-400" aria-hidden />
                      Чат
                    </p>
                  </div>
                </>
              )}
            </header>

            <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              ref={scroller}
              className="scrollbar-dark flex-1 overflow-y-auto px-4 py-5 sm:px-6"
            >
              {selected.size !== 1 || history.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center">
                  <span className="flex size-14 items-center justify-center rounded-2xl border border-brand/20 bg-brand/10 text-brand">
                    <MdOutlineChat className="text-2xl" aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold text-white">Нове повідомлення</p>
                    <p className="mt-1 max-w-xs text-sm text-neutral-500">
                      Оберіть одного або кількох партнерів ліворуч, напишіть текст та додайте
                      медіа
                    </p>
                  </div>
                </div>
              ) : (
                <ul className="flex flex-col gap-4 w-full xl:w-4/5 xl:mx-auto">
                  {history.map((m) => (
                    <li key={m.batch_id} className="fade-in ml-auto flex max-w-[85%] flex-col items-end sm:max-w-[70%]">
                      <div className="w-full rounded-2xl rounded-br-md bg-brand/15 p-2 shadow-lg shadow-black/20">
                        {m.body && (
                          <p className="whitespace-pre-wrap break-words px-2 py-1 text-sm text-white">
                            {m.body}
                          </p>
                        )}
                      </div>
                      <p className="flex items-center gap-1.5 px-1 text-[11px] text-neutral-500">
                        {(selected.size > 1 || m.recipient_ids.length > 1) && (
                          <span className="truncate">→ {recipientsLabel(m.recipient_ids)} ·</span>
                        )}
                        {formatTime(m.created_at)}
                        <LuCheckCheck className="text-brand" aria-hidden />
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <form onSubmit={handleSend} className="relative px-3 pb-3 xl:pb-8 w-full xl:w-3/5 xl:mx-auto">
              {pending.length > 0 && (
                <div className="absolute -top-3 -translate-y-full flex items-end flex-wrap gap-3">
                  {pending.map((p, idx) => (
                    <div key={`${p.file.name}-${idx}`} className="group relative">
                      {p.preview ? (
                        <img
                          src={p.preview}
                          alt={p.file.name}
                          className="h-14 w-20 rounded-lg border border-line object-cover"
                        />
                      ) : (
                        <div className="flex max-w-52 items-center gap-2 rounded-lg border border-line bg-white/5 px-2 py-2">
                          <LuFileText className="shrink-0 text-lg text-brand" aria-hidden />
                          <span className="min-w-0">
                            <span className="block truncate text-xs text-white">{p.file.name}</span>
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        aria-label="Видалити файл"
                        className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-neutral-800 text-white ring-2 ring-panel transition hover:bg-red-500"
                      >
                        <LuX className="text-xs" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {sendError && <p className="mb-2 text-xs text-red-400 px-2">{sendError}</p>}
              {notice && !sendError && (
                <p className="mb-2 flex items-center gap-1.5 text-xs text-green-500 px-2">
                  <LuCheckCheck aria-hidden />
                  {notice}
                </p>
              )}

              <div className="flex items-start gap-1 rounded-xl border-2 border-line bg-white/5 p-1.5 transition focus-within:border-brand/60">
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  aria-label="Прикріпити файл"
                  title="Прикріпити файл"
                  className="flex size-10 shrink-0 items-center justify-center rounded-full text-neutral-400 transition hover:bg-white/5 hover:text-brand"
                >
                  <LuPaperclip className="text-lg" aria-hidden />
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <textarea
                  rows={1}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setNotice(null);
                  }}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  placeholder="Напишіть повідомлення…"
                  className="[field-sizing:content] my-auto max-h-44 flex-1 resize-none bg-transparent px-1 py-2 text-sm text-white outline-none placeholder:text-neutral-500"
                />
                {canSend && (
                  <button
                    type="submit"
                    disabled={!canSend}
                    aria-label="Надіслати"
                    title={selected.size === 0 ? "Оберіть отримувачів" : "Надіслати"}
                    className="flex size-9 mt-0.5 mr-0.5 shrink-0 items-center justify-center rounded-lg text-white hover:bg-brand/10 hover:text-brand transition hover:opacity-90 disabled:cursor-not-allowed disabled:text-neutral-500"
                  >
                    {sending ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <LuSend className="text-lg" aria-hidden />
                    )}
                  </button>
                )}
              </div>
            </form>
            {dragging && (
              <div className="pointer-events-none absolute inset-2 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-brand/60 bg-night/85 text-brand backdrop-blur-sm">
                <LuImagePlus className="text-4xl" aria-hidden />
                <p className="text-sm font-semibold">Перетягніть файли сюди</p>
              </div>
            )}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};
